import { Navigate, Outlet, useLocation } from "react-router";
import { useAuth } from "@/lib/auth-context";
import { useProjeto } from "@/lib/projeto-context";
import { Skeleton } from "@/components/ui/skeleton";
import { PERMISSION_ACTIONS } from "@/lib/constants";

/**
 * Guarda de rota.
 *
 * A ordem das verificações importa — cada uma tem o seu destino próprio, para
 * o utilizador perceber POR QUE motivo não entrou:
 *
 *   1. ainda a carregar        → esqueleto (nunca redirecionar durante o loading,
 *                                ou um refresh de página atira toda a gente para o login)
 *
 * ⚠️ O ponto 1 inclui `hydrating`, e não só `loading`. A sessão resolve-se
 *    bem antes do perfil chegar; nessa janela `profile` é null e `isApproved`
 *    é false. Sem esperar pela hidratação, um utilizador aprovado é atirado
 *    para /aguardando-aprovacao — e fica lá preso, porque esse ecrã está fora
 *    da árvore protegida e nunca reavalia. Foi exatamente o que aconteceu.
 *   2. sem sessão              → /login?next=<origem>
 *   3. conta por aprovar       → /aguardando-aprovacao
 *   4. sem projeto atribuído   → /sem-projeto
 *   5. sem permissão de leitura → /sem-permissao
 *
 * Isto é conveniência de UI, não segurança. Quem protege os dados é a RLS: uma
 * query direta sem permissão devolve vazio, independentemente desta guarda.
 */
export function ProtectedRoute({ modulo }: { modulo?: string }) {
  const { session, loading, hydrating, isApproved, hasPermission } = useAuth();
  const { projetoAtivo, loading: projetoLoading } = useProjeto();
  const location = useLocation();

  if (loading || hydrating || projetoLoading) {
    return (
      <div className="flex min-h-svh flex-col gap-4 p-8">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!session) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (!isApproved) {
    return <Navigate to="/aguardando-aprovacao" replace />;
  }

  if (!projetoAtivo) {
    return <Navigate to="/sem-projeto" replace />;
  }

  if (modulo && !hasPermission(modulo, PERMISSION_ACTIONS.READ)) {
    return <Navigate to="/sem-permissao" replace />;
  }

  return <Outlet />;
}

/**
 * Guarda das rotas de administração.
 *
 * Esconder o link na sidebar não é proteção — um URL direto abria o ecrã na
 * mesma. A RLS recusaria as escritas, mas a lista de utilizadores já seria
 * visível, e isso é uma fuga.
 *
 * Assume-se aninhada dentro de `ProtectedRoute`, que já tratou de sessão,
 * aprovação e projeto ativo.
 */
export function AdminRoute({ papeis }: { papeis?: string[] } = {}) {
  const { isAdmin, papel, loading, hydrating } = useAuth();

  if (loading || hydrating) {
    return (
      <div className="grid gap-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // Admin/Master entram sempre. Fora disso, só os papéis explicitamente
  // autorizados — p.ex. o Gestor na página de Catálogos (Sprint VI). Sem
  // `papeis`, a rota é só-admin, como as de Projetos e Utilizadores.
  const autorizado = isAdmin || (!!papel && (papeis ?? []).includes(papel));
  if (!autorizado) return <Navigate to="/sem-permissao" replace />;

  return <Outlet />;
}
